import { useEffect, useMemo } from 'react';
import { t } from 'i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixAutocomplete, RadixButton, RadixDialog, RadixInput } from '@/components/radix';
import type { RadixAutocompleteOption } from '@/components/radix';
import { useProductService } from '@/hooks/api/pos/products/useProducts';
import { useMediaQuery } from '@/hooks/shared';
import type { ProductOptionRow } from './types';
import { ServiceFormulaModalValues, ServiceFormulaUpsertModalProps } from './types';

const SERVICE_FORMULA_UPSERT_FORM_ID = 'service-formula-upsert-form';

function formatBaseUnitQuantityForDisplay(value: number) {
    if (!Number.isFinite(value)) return '';
    if (Number.isInteger(value)) return String(value);
    return String(value);
}

export default function ServiceFormulaUpsertModal({
    open,
    onOpenChange,
    mode,
    initialValues,
    unitsById,
    onSubmit,
    isSubmittingExternal = false,
}: ServiceFormulaUpsertModalProps) {
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const productSearch = useProductService({ text: '', page: 1, limit: 100, getServices: false }, 0, 0, open, false);

    /** Listing API: `products[]` are categories; each category has `products[]` (actual SKUs). */
    const productOptions = useMemo((): ProductOptionRow[] => {
        const categories = productSearch.data?.products ?? [];
        return categories.flatMap((category) =>
            (category.products ?? []).map(
                (product) =>
                    ({
                        ...product,
                        categoryId: category.id,
                        categoryName: category.name,
                    }) as ProductOptionRow,
            ),
        );
    }, [productSearch.data?.products]);

    const validationSchema = Yup.object({
        productId: Yup.string().required(t('Services.InventoryFormulasValidationProductRequired')),
        consumeQty: Yup.string()
            .required(t('Services.InventoryFormulasValidationQtyRequired'))
            .test(
                'positive-qty',
                t('Services.InventoryFormulasValidationQtyRequired'),
                (val) => Number.isFinite(Number(val)) && Number(val) > 0,
            ),
    });

    const formik = useFormik<ServiceFormulaModalValues>({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await onSubmit(values);
                onOpenChange(false);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const productSelectOptions: RadixAutocompleteOption[] = useMemo(
        () =>
            productOptions.map((p) => ({
                value: String(p.id),
                searchText: `${p.name ?? ''} ${p.categoryName ?? ''} ${p.sku ?? ''}`,
                label: (
                    <div className="flex flex-col items-start leading-tight">
                        <span className="text-sm text-text-primary">
                            {p.name}
                            {String(p.categoryId) !== '0' ? ` (${p.categoryName})` : ''}
                        </span>
                        {p.sku ? <span className="text-xs text-text-secondary">{p.sku}</span> : null}
                    </div>
                ),
            })),
        [productOptions],
    );

    const { values, errors, touched, isSubmitting, handleSubmit, setFieldValue, resetForm } = formik;

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open, resetForm]);

    const submitDisabled = isSubmitting || isSubmittingExternal;

    const selectedProduct = productOptions.find((p) => String(p.id) === values.productId);
    const hasBaseUnitOnProduct = Boolean(selectedProduct?.baseUnitId);
    const listingBaseUnitId =
        selectedProduct?.baseUnitId != null && selectedProduct.baseUnitId !== ''
            ? String(selectedProduct.baseUnitId)
            : undefined;
    const quantityInputAbbreviation =
        selectedProduct?.unit?.abbreviation?.trim() ||
        (listingBaseUnitId ? unitsById[listingBaseUnitId]?.abbreviation : undefined) ||
        (!hasBaseUnitOnProduct && selectedProduct ? t('Common.DefaultProductUnit') : undefined);

    const showBaseUnitConversionHint =
        selectedProduct != null &&
        hasBaseUnitOnProduct &&
        typeof selectedProduct.baseUnitQuantity === 'number' &&
        Number.isFinite(selectedProduct.baseUnitQuantity) &&
        selectedProduct.baseUnitQuantity > 0 &&
        Boolean((quantityInputAbbreviation ?? '').trim());

    return (
        <RadixDialog
            open={open}
            position={isDesktop ? 'center' : 'bottom'}
            onOpenChange={onOpenChange}
            title={t('Services.AddProduct')}
            contentClassName="p-4"
            footer={
                <div className="flex items-center justify-end gap-2 w-full">
                    <RadixButton
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitDisabled}
                    >
                        {t('Setting.Cancel')}
                    </RadixButton>
                    <RadixButton type="submit" form={SERVICE_FORMULA_UPSERT_FORM_ID} disabled={submitDisabled}>
                        {mode === 'create' ? t('Common.Add') : t('Common.Save')}
                    </RadixButton>
                </div>
            }
        >
            <form id={SERVICE_FORMULA_UPSERT_FORM_ID} className="space-y-4" onSubmit={handleSubmit} noValidate>
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-text-primary">
                            {t('Services.InventoryFormulasFieldProduct')}
                        </div>
                    </div>
                    <RadixAutocomplete
                        options={productSelectOptions}
                        isLoading={productSearch.isLoading}
                        value={values.productId || undefined}
                        placeholder={t('Services.InventoryFormulasSelectProductPlaceholder')}
                        searchPlaceholder={t('Common.Search')}
                        onValueChange={(value) => {
                            setFieldValue('productId', value);
                            if (mode === 'create') {
                                setFieldValue('consumeQty', '1');
                            }
                        }}
                    />
                    {touched.productId && errors.productId && (
                        <div className="text-xs text-red-600">{errors.productId}</div>
                    )}
                </div>

                {showBaseUnitConversionHint && selectedProduct ? (
                    <div className="text-sm text-text-secondary leading-snug">
                        {t('Services.InventoryFormulasOneStockUnitEqualsBase', {
                            productName: selectedProduct.name,
                            stockUnit: t('Common.DefaultProductUnit'),
                            quantity: formatBaseUnitQuantityForDisplay(selectedProduct.baseUnitQuantity),
                            abbreviation: quantityInputAbbreviation ?? '',
                        })}
                    </div>
                ) : null}
                {selectedProduct && !hasBaseUnitOnProduct ? (
                    <div className="text-sm text-text-secondary leading-snug whitespace-pre-line">
                        {t('Services.InventoryFormulasNoUnitNosPerService')}
                    </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-text-primary">
                            {selectedProduct && !hasBaseUnitOnProduct
                                ? t('Services.InventoryFormulasFieldQty')
                                : t('Services.InventoryFormulasFieldQty')}
                        </div>

                        <RadixInput
                            inputMode="decimal"
                            value={values.consumeQty}
                            onChange={(e) => setFieldValue('consumeQty', e.target.value)}
                            placeholder={selectedProduct && !hasBaseUnitOnProduct ? '1' : '0'}
                            endComponent={
                                <span className="text-xs text-text-secondary">{quantityInputAbbreviation ?? ''}</span>
                            }
                        />
                        {touched.consumeQty && errors.consumeQty && (
                            <div className="text-xs text-red-600">{errors.consumeQty}</div>
                        )}
                    </div>
                </div>
            </form>
        </RadixDialog>
    );
}
